import { Fragment, useMemo, useState } from 'react'
import { AlertCircle, ChevronDown, ChevronRight, RefreshCw, Search, Wallet } from 'lucide-react'
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Label,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui'
import { LoadingTable } from '../ui/loading-states'
import { useSalariesReport } from '../../hooks/useReports'
import { useOrganizationsForSelect } from '../../hooks/organizations/useOrganizations'
import { useDepartmentsForSelect } from '../../hooks/useDepartments'
import { exportSalariesToExcel } from '../../utils/exportSalariesToExcel'
import type {
  SalariesRecord,
  SalariesReportFilters,
  SalariesReportResponse,
} from '../../types/reports.types'

interface Organization {
  id: string
  name: string
}

interface Department {
  id: string
  name: string
}

function previousPeriod(): string {
  const d = new Date()
  // Previous calendar month — payroll is computed after month-end, so the
  // most recent period that's guaranteed to have data is the previous one.
  const prev = new Date(d.getFullYear(), d.getMonth() - 1, 1)
  return `${prev.getFullYear()}-${String(prev.getMonth() + 1).padStart(2, '0')}`
}

function formatKzt(n: number): string {
  return new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 0 }).format(n)
}

export function SalariesReport() {
  const [filters, setFilters] = useState<SalariesReportFilters>({
    period: previousPeriod(),
  })
  const [shouldFetch, setShouldFetch] = useState(false)
  const [expanded, setExpanded] = useState<Set<string>>(new Set())

  const { data: organizations } = useOrganizationsForSelect() as {
    data: Organization[] | undefined
  }
  const { data: departments } = useDepartmentsForSelect(filters.organizationId || null) as {
    data: Department[] | undefined
  }

  const { data: report, isLoading, isFetching, error, refetch } = useSalariesReport(
    filters,
    shouldFetch,
  )

  const handleGenerate = () => {
    setShouldFetch(true)
    if (shouldFetch) refetch()
  }

  const toggleRow = (employeeId: string) => {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(employeeId)) next.delete(employeeId)
      else next.add(employeeId)
      return next
    })
  }

  const handleExport = async () => {
    if (report && report.data.length > 0) {
      try {
        await exportSalariesToExcel(report)
      } catch (e) {
        console.error('Salaries export failed:', e)
      }
    }
  }

  const canExport = !!report && report.data.length > 0

  return (
    <div className="space-y-6">
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap items-end gap-4">
            <div className="space-y-2">
              <Label htmlFor="salaries-period">Период (месяц)</Label>
              <input
                id="salaries-period"
                type="month"
                value={filters.period}
                onChange={(e) => setFilters((p) => ({ ...p, period: e.target.value }))}
                className="h-10 w-[180px] rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="salaries-org">Организация</Label>
              <select
                id="salaries-org"
                value={filters.organizationId || ''}
                onChange={(e) =>
                  setFilters((p) => ({
                    ...p,
                    organizationId: e.target.value || undefined,
                    departmentId: undefined,
                  }))
                }
                className="h-10 w-[250px] rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                <option value="">Все организации</option>
                {organizations?.map((org) => (
                  <option key={org.id} value={org.id}>
                    {org.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="salaries-dept">Подразделение</Label>
              <select
                id="salaries-dept"
                value={filters.departmentId || ''}
                onChange={(e) =>
                  setFilters((p) => ({ ...p, departmentId: e.target.value || undefined }))
                }
                disabled={!filters.organizationId}
                className="h-10 w-[250px] rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <option value="">Все подразделения</option>
                {departments?.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex gap-2">
              <Button onClick={handleGenerate} disabled={isLoading || isFetching} className="gap-2">
                {isLoading || isFetching ? (
                  <RefreshCw className="h-4 w-4 animate-spin" />
                ) : (
                  <Search className="h-4 w-4" />
                )}
                Сформировать отчет
              </Button>
              <Button
                variant="default"
                className="bg-green-600 hover:bg-green-700"
                disabled={!canExport}
                onClick={handleExport}
              >
                <span className="mr-2">📊</span>
                Экспорт в Excel
              </Button>
              <Button
                variant="outline"
                onClick={() =>
                  setFilters({
                    period: filters.period,
                    organizationId: undefined,
                    departmentId: undefined,
                  })
                }
              >
                Сбросить
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {error && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-red-600">
              <AlertCircle className="h-5 w-5" />
              <p>Ошибка загрузки отчёта. Попробуйте ещё раз.</p>
            </div>
          </CardContent>
        </Card>
      )}

      {shouldFetch && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Wallet className="h-5 w-5" />
              Зарплаты сотрудников
            </CardTitle>
            <CardDescription>
              {report ? (
                <ReportSummary report={report} />
              ) : (
                'Загрузка…'
              )}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading || isFetching ? (
              <LoadingTable rows={10} />
            ) : report && report.data.length > 0 ? (
              <>
                {report.meta.total_with_data === 0 && (
                  <div className="mb-4 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                    <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <div>
                      В период <span className="font-semibold">{report.meta.period}</span> ни у одного сотрудника ещё нет данных в&nbsp;salary_data — возможно, месяц не закрыт или синхронизация не запущена. Попробуйте предыдущий месяц или нажмите «Синхронизировать KPI-бонусы» в Настройках.
                    </div>
                  </div>
                )}
                <SalariesTable
                  rows={report.data}
                  totals={report.meta.totals}
                  expanded={expanded}
                  onToggleRow={toggleRow}
                />
              </>
            ) : (
              <div className="text-center py-8">
                <Wallet className="mx-auto h-12 w-12 text-muted-foreground/50" />
                <h3 className="mt-2 text-sm font-semibold text-muted-foreground">Нет данных</h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  В выбранных фильтрах нет ни одного сотрудника.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {!shouldFetch && (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-8">
              <Wallet className="mx-auto h-12 w-12 text-muted-foreground/50" />
              <h3 className="mt-2 text-sm font-semibold text-muted-foreground">
                Выберите период и нажмите «Сформировать отчёт»
              </h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Отчёт показывает оклад, KPI-бонусы и итоговую сумму к выплате по каждому сотруднику.
              </p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}

function ReportSummary({ report }: { report: SalariesReportResponse }) {
  const m = report.meta
  return (
    <>
      Период: {m.period} | Сотрудников: {m.total_employees} | С данными:{' '}
      {m.total_with_data} | Итого к выплате:{' '}
      <span className="font-semibold">{formatKzt(m.totals.net_amount)} ₸</span>
    </>
  )
}

function SalariesTable({
  rows,
  totals,
  expanded,
  onToggleRow,
}: {
  rows: SalariesRecord[]
  totals: SalariesReportResponse['meta']['totals']
  expanded: Set<string>
  onToggleRow: (id: string) => void
}) {
  const indexed = useMemo(
    () =>
      rows.map((r, idx) => ({ idx: idx + 1, row: r, isExpanded: expanded.has(r.employee_id) })),
    [rows, expanded],
  )

  return (
    <div className="rounded-md border overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[40px]"></TableHead>
            <TableHead className="w-[40px]">#</TableHead>
            <TableHead>Сотрудник</TableHead>
            <TableHead>Таб. №</TableHead>
            <TableHead>Организация</TableHead>
            <TableHead>Подразделение</TableHead>
            <TableHead>Должность</TableHead>
            <TableHead className="text-right">Оклад, ₸</TableHead>
            <TableHead className="text-right">Бонусы, ₸</TableHead>
            <TableHead className="text-right">Удержания, ₸</TableHead>
            <TableHead className="text-right">К выплате, ₸</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {indexed.map(({ idx, row, isExpanded }) => {
            const hasBreakdown = row.bonuses_breakdown.length > 0
            return (
              <Fragment key={row.employee_id}>
                <TableRow
                  className={row.is_missing ? 'text-muted-foreground' : ''}
                >
                  <TableCell>
                    {hasBreakdown ? (
                      <button
                        type="button"
                        onClick={() => onToggleRow(row.employee_id)}
                        className="inline-flex items-center justify-center rounded-md p-1 hover:bg-muted"
                        aria-label={isExpanded ? 'Свернуть' : 'Раскрыть детализацию'}
                      >
                        {isExpanded ? (
                          <ChevronDown className="h-4 w-4" />
                        ) : (
                          <ChevronRight className="h-4 w-4" />
                        )}
                      </button>
                    ) : null}
                  </TableCell>
                  <TableCell className="font-medium">{idx}</TableCell>
                  <TableCell className="font-medium">
                    {row.employee_name}
                    {row.is_missing && (
                      <span className="ml-2 inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-700">
                        нет данных
                      </span>
                    )}
                    {row.synced_from_kpi && (
                      <span className="ml-2 inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-xs text-blue-700">
                        KPI
                      </span>
                    )}
                  </TableCell>
                  <TableCell>{row.employee_number || '-'}</TableCell>
                  <TableCell>{row.organization_name || '-'}</TableCell>
                  <TableCell>{row.department_name || '-'}</TableCell>
                  <TableCell>{row.position_name || '-'}</TableCell>
                  <TableCell className="text-right">{formatKzt(row.amount)}</TableCell>
                  <TableCell className="text-right">{formatKzt(row.bonuses)}</TableCell>
                  <TableCell className="text-right">{formatKzt(row.deductions)}</TableCell>
                  <TableCell className="text-right font-semibold">
                    {formatKzt(row.net_amount)}
                  </TableCell>
                </TableRow>
                {isExpanded && hasBreakdown && (
                  <TableRow className="bg-muted/30">
                    <TableCell></TableCell>
                    <TableCell colSpan={10} className="text-sm">
                      <div className="space-y-1 py-2">
                        <div className="font-medium text-xs uppercase text-muted-foreground">
                          Детализация бонусов
                        </div>
                        <ul className="space-y-1">
                          {row.bonuses_breakdown.map((b, i) => (
                            <li key={i} className="flex justify-between">
                              <span>
                                {b.organization} · {b.department}
                              </span>
                              <span className="font-medium">{formatKzt(b.sum)} ₸</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </Fragment>
            )
          })}

          {/* Totals row */}
          <TableRow className="bg-muted/50 font-semibold">
            <TableCell></TableCell>
            <TableCell></TableCell>
            <TableCell colSpan={5}>Итого</TableCell>
            <TableCell className="text-right">{formatKzt(totals.amount)}</TableCell>
            <TableCell className="text-right">{formatKzt(totals.bonuses)}</TableCell>
            <TableCell className="text-right">{formatKzt(totals.deductions)}</TableCell>
            <TableCell className="text-right">{formatKzt(totals.net_amount)}</TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </div>
  )
}
